import {
  type AchievementId,
  type AchievementView,
  type EarnedAchievement,
  type GlobalAchievementValues,
} from "@dailywars/core/achievements";
import { Link } from "@tanstack/react-router";
import { ArrowRightIcon, CircleQuestionMarkIcon } from "lucide-react";

import { Button } from "#/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "#/components/ui/card";
import { Checkbox } from "#/components/ui/checkbox";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "#/components/ui/empty";
import { Infotip, InfotipContent, InfotipProvider, InfotipTrigger } from "#/components/ui/infotip";
import { Separator } from "#/components/ui/separator";
import { Skeleton } from "#/components/ui/skeleton";
import { cn } from "#/lib/utils";

import {
  GlobalAchievementProgress,
  OverallAchievementProgress,
} from "./-profile.achievementProgress";

export { OverallAchievementProgress };

const earnedDateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeZone: "UTC",
});

const achievementNumberFormatter = new Intl.NumberFormat("en-US");

function formatAchievementEarnCountLabel(earnCount: number): string {
  const countLabel = achievementNumberFormatter.format(earnCount);
  return earnCount === 1 ? `Earned by ${countLabel} player` : `Earned by ${countLabel} players`;
}

function AchievementEarnCountInfotip({ earnCount }: { earnCount: number }) {
  const label = formatAchievementEarnCountLabel(earnCount);
  return (
    <Infotip>
      <InfotipTrigger
        aria-label={label}
        className="inline-flex text-muted-foreground transition-colors hover:text-foreground"
      >
        <CircleQuestionMarkIcon className="size-4" />
      </InfotipTrigger>
      <InfotipContent>{label}</InfotipContent>
    </Infotip>
  );
}

function AchievementListItem({
  achievement,
  achievementValues,
  earnCount,
}: {
  achievement: AchievementView;
  achievementValues?: GlobalAchievementValues;
  earnCount?: number;
}) {
  return (
    <div className="flex items-start gap-4 py-4">
      <Checkbox
        checked={achievement.earnedAt !== undefined}
        readOnly
        tabIndex={-1}
        aria-label={`${achievement.title} ${
          achievement.earnedAt === undefined ? "not earned" : "earned"
        }`}
        className="mt-0.5 size-6"
      />
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "font-heading font-medium",
            achievement.earnedAt !== undefined && "text-muted-foreground",
          )}
        >
          {achievement.title}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{achievement.description}</p>
        <GlobalAchievementProgress achievement={achievement} values={achievementValues} />
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2">
        {earnCount !== undefined ? <AchievementEarnCountInfotip earnCount={earnCount} /> : null}
        {achievement.earnedAt !== undefined ? (
          <p className="text-xs text-muted-foreground">
            {earnedDateFormatter.format(achievement.earnedAt)}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function AchievementList({
  achievements,
  achievementValues,
  achievementEarnCounts,
}: {
  achievements: AchievementView[];
  achievementValues?: GlobalAchievementValues;
  achievementEarnCounts?: Partial<Record<AchievementId, number>>;
}) {
  const list = (
    <div className="flex flex-col">
      {achievements.map((achievement, index) => (
        <div key={achievement.id}>
          {index > 0 ? <Separator /> : null}
          <AchievementListItem
            achievement={achievement}
            achievementValues={achievementValues}
            earnCount={
              achievementEarnCounts
                ? (achievementEarnCounts[achievement.id as AchievementId] ?? 0)
                : undefined
            }
          />
        </div>
      ))}
    </div>
  );

  if (!achievementEarnCounts) return list;
  return <InfotipProvider delay={200}>{list}</InfotipProvider>;
}

export function AchievementsCard({
  achievements,
  displayName,
  earnedCount,
}: {
  achievements: EarnedAchievement[];
  displayName: string;
  earnedCount: number;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Achievements</CardTitle>
        <CardDescription>Recently earned achievements.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <OverallAchievementProgress earnedCount={earnedCount} />
        {achievements.length === 0 ? (
          <Empty className="border-0 p-4">
            <EmptyHeader>
              <EmptyTitle>No achievements yet</EmptyTitle>
              <EmptyDescription>
                Complete games and build your record to earn achievements.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <AchievementList achievements={achievements} />
        )}
      </CardContent>
      <CardFooter>
        <Button
          variant="default"
          className="mx-auto"
          render={
            <Link to="/profile/$name/achievements" params={{ name: displayName }} preload={false} />
          }
          nativeButton={false}
        >
          View all achievements
          <ArrowRightIcon data-icon="inline-end" />
        </Button>
      </CardFooter>
    </Card>
  );
}

export function AchievementsCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-4 w-72 max-w-full" />
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <Skeleton className="h-8 w-full rounded-full" />
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className="flex flex-col gap-2">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="h-4 w-64 max-w-full" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
